/*
 * Makes the PayPal "Pay Later" messaging reflect the tax-included price.
 *
 * Prices in this store are entered without tax. Two placements are handled:
 *
 *  - cart: Shopify's upstream cart message is re-injected/re-rendered on every
 *    cart change, so we simply overwrite its `data-pp-amount` with the
 *    tax-included value before it renders (a MutationObserver keeps it in sync).
 *
 *  - product: Shopify's upstream product message renders only once and caches
 *    its amount, so it never follows a variant change (and it even resets
 *    data-pp-amount back to that cached value). We therefore hide it and render
 *    our OWN message — through the merchant's PayPal Messages SDK — into a
 *    separate container, computing the amount from the SELECTED VARIANT price
 *    (read from the theme, always fresh) and re-rendering on every variant
 *    change.
 *
 * Loaded once per placement:
 *   <script src=".../paypal-tax-amount.js"
 *           data-pp-placement="product|cart" data-pp-tax-rate="21" defer></script>
 */
(function () {
  var loader = document.currentScript;
  var rate = parseFloat(loader && loader.getAttribute('data-pp-tax-rate'));
  var placement = (loader && loader.getAttribute('data-pp-placement')) || 'product';
  if (!rate || rate <= 0) return;

  var controller = window.__paypalTaxAmount;

  if (!controller) {
    controller = window.__paypalTaxAmount = (function () {
      var multipliers = {}; // placement -> multiplier (e.g. 1.21)
      var computed = new WeakMap(); // cart element -> value we wrote
      var ownContainers = new WeakMap(); // product element -> our render container

      function formatAmount(amount) {
        return (Math.round(amount * 100) / 100).toFixed(2);
      }

      // Reads the data-pp-style-* attributes into the shape the PayPal Messages
      // SDK expects, so our message matches the original look.
      function readStyle(element) {
        var style = {};
        var layout = element.getAttribute('data-pp-style-layout');
        if (layout) style.layout = layout;
        var logoType = element.getAttribute('data-pp-style-logo-type');
        if (logoType) style.logo = { type: logoType };
        var color = element.getAttribute('data-pp-style-text-color');
        var size = element.getAttribute('data-pp-style-text-size');
        var align = element.getAttribute('data-pp-style-text-align');
        if (color || size || align) {
          style.text = {};
          if (color) style.text.color = color;
          if (size) style.text.size = parseInt(size, 10);
          if (align) style.text.align = align;
        }
        return style;
      }

      function sdkReady() {
        return !!(window.PayPalSDK && window.PayPalSDK.Messages);
      }
      function whenSdkReady(callback) {
        if (sdkReady()) return callback();
        var tries = 0;
        var timer = setInterval(function () {
          if (sdkReady()) {
            clearInterval(timer);
            callback();
          } else if (++tries > 60) {
            clearInterval(timer);
          }
        }, 100);
      }

      /* ----------------- cart: overwrite data-pp-amount --------------------- */
      function applyCart(element) {
        var multiplier = multipliers['cart'];
        if (!multiplier) return;
        var current = element.getAttribute('data-pp-amount');
        if (current == null || current === computed.get(element)) return;
        var base = parseFloat(current);
        if (!isFinite(base) || base <= 0) return;
        var withTax = formatAmount(base * multiplier);
        if (element.getAttribute('data-pp-amount') === withTax) return;
        computed.set(element, withTax);
        element.setAttribute('data-pp-amount', withTax);
      }

      function refreshCart() {
        document
          .querySelectorAll('[data-pp-message][data-pp-placement="cart"]')
          .forEach(applyCart);
      }

      /* ----------- product: render our own message from variant price ------- */
      // Selected variant price (major units) from the theme — always fresh,
      // unlike the contested data-pp-amount. Falls back to the element's initial
      // amount for products without a variant picker.
      function productBase(element) {
        var root = (element.closest && element.closest('product-info')) || document;
        var node = root.querySelector('variant-selects [data-selected-variant]');
        if (node) {
          try {
            var variant = JSON.parse(node.textContent);
            if (variant && variant.price != null) return variant.price / 100;
          } catch (e) {
            /* fall through to the attribute fallback */
          }
        }
        var attr = parseFloat(element.getAttribute('data-pp-amount'));
        return isFinite(attr) && attr > 0 ? attr : null;
      }

      function renderProduct(element) {
        var multiplier = multipliers['product'];
        if (!multiplier) return;

        // Hide the platform message (it can't follow variant changes) and make
        // our own container the visible one.
        var container = ownContainers.get(element);
        if (!container) {
          container = document.createElement('div');
          container.className = 'pp-tax-message';
          element.style.display = 'none';
          element.parentNode.insertBefore(container, element.nextSibling);
          ownContainers.set(element, container);
        }

        clearTimeout(element.__ppRenderTimer);
        element.__ppRenderTimer = setTimeout(function () {
          var base = productBase(element);
          if (base == null || !(base > 0)) return;
          var amount = Math.round(base * multiplier * 100) / 100;
          whenSdkReady(function () {
            try {
              container.innerHTML = '';
              window.PayPalSDK.Messages({
                amount: amount,
                placement: 'product',
                style: readStyle(element),
              }).render(container);
            } catch (e) {
              /* keep the empty container rather than break the page */
            }
          });
        }, 200);
      }

      function refreshProducts() {
        document
          .querySelectorAll('[data-pp-message][data-pp-placement="product"]')
          .forEach(renderProduct);
      }

      /* ------------------------- observers / triggers ----------------------- */
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var mutation = mutations[i];
          if (mutation.type === 'attributes') {
            var target = mutation.target;
            if (target.getAttribute && target.getAttribute('data-pp-placement') === 'cart') {
              applyCart(target);
            }
          } else if (mutation.type === 'childList') {
            for (var j = 0; j < mutation.addedNodes.length; j++) {
              var node = mutation.addedNodes[j];
              if (node.nodeType !== 1) continue;
              var found = [];
              if (node.matches && node.matches('[data-pp-message][data-pp-placement]')) found.push(node);
              if (node.querySelectorAll) {
                node.querySelectorAll('[data-pp-message][data-pp-placement]').forEach(function (el) {
                  found.push(el);
                });
              }
              found.forEach(function (el) {
                var p = el.getAttribute('data-pp-placement');
                if (p === 'cart') applyCart(el);
                else if (p === 'product') renderProduct(el);
              });
            }
          }
        }
      });

      function start() {
        observer.observe(document.documentElement, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['data-pp-amount'],
        });
        refreshCart();
        refreshProducts();
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
      } else {
        start();
      }

      // Re-render the product message on variant change, from the fresh
      // selected-variant price (the event payload itself can be stale).
      document.addEventListener('variant:changed', function () {
        if (multipliers['product'] != null) refreshProducts();
      });

      return {
        register: function (placementName, taxRate) {
          multipliers[placementName] = 1 + taxRate / 100;
          if (placementName === 'cart') refreshCart();
          else if (placementName === 'product') refreshProducts();
        },
      };
    })();
  }

  controller.register(placement, rate);
})();
